<script setup>
import { ref, onMounted } from 'vue'
import CardLichen from '@/design/primitives/CardLichen.vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const inventory = ref([])
const loading   = ref(false)
const error     = ref(null)
let   fetched   = false

async function load() {
  if (fetched) return
  loading.value = true
  error.value   = null
  try {
    const res = await api.get(EP.userInventory)
    const data = res.data
    if (Array.isArray(data))               inventory.value = data
    else if (Array.isArray(data?.items))   inventory.value = data.items
    else if (Array.isArray(data?.inventory)) inventory.value = data.inventory
    else                                   inventory.value = []
    fetched = true
  } catch (e) {
    error.value = e.message || 'Could not load inventory.'
  } finally {
    loading.value = false
  }
}

function itemName(item) {
  return item.name ?? item.itemName ?? '—'
}

function itemQty(item) {
  return item.quantity ?? item.count ?? item.qty ?? 1
}

function itemImage(item) {
  return item.imageUrl ?? item.image ?? item.img ?? null
}

onMounted(load)
</script>

<template>
  <section class="dossier-section" aria-labelledby="inventory-heading">
    <h2 id="inventory-heading" class="stamp u-display u-allcaps">Inventory</h2>

    <PanelOrganic tone="strong" :padded="true">
    <!-- Loading -->
    <div v-if="loading" class="ledger-state" aria-live="polite">
      <span class="dossier-spinner" aria-hidden="true" />
      <span class="ledger-state__text u-display u-allcaps">Loading inventory…</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="ledger-state ledger-state--err" aria-live="assertive">
      <span class="ledger-state__text">{{ error }}</span>
      <ButtonSeed variant="ghost" size="sm" @click="() => { fetched = false; load() }">RETRY</ButtonSeed>
    </div>

    <!-- Empty -->
    <div v-else-if="!inventory.length" class="ledger-state ledger-state--empty">
      <span class="u-display u-allcaps">Your in-game inventory is empty</span>
    </div>

    <!-- 4-col poster grid -->
    <ul v-else class="poster-grid" role="list" aria-label="Inventory items">
      <li v-for="(item, i) in inventory" :key="item.blueprint ?? item.name ?? i">
        <CardLichen :hoverable="true">
          <div class="poster-card">
            <!-- Item image or placeholder -->
            <div class="poster-card__image-wrap">
              <img
                v-if="itemImage(item)"
                :src="itemImage(item)"
                :alt="itemName(item)"
                class="poster-card__image"
                loading="lazy"
              />
              <div v-else class="poster-card__image-placeholder" aria-hidden="true">
                <span class="u-display u-allcaps">{{ itemName(item).slice(0, 2) }}</span>
              </div>
            </div>

            <!-- Name -->
            <p class="poster-card__name u-display u-allcaps">{{ itemName(item) }}</p>

            <!-- Quantity -->
            <p class="poster-card__qty u-mono u-gold">× {{ itemQty(item) }}</p>
          </div>
        </CardLichen>
      </li>
    </ul>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

/* Poster grid — 4 columns */
.poster-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--sp-4);
}

.poster-card {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.poster-card__image-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: var(--r-xs);
  overflow: hidden;
  background: var(--bg-deep);
  margin-bottom: var(--sp-2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.poster-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.poster-card__image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-glass);
}

.poster-card__image-placeholder span {
  font-size: var(--fs-26);
  color: var(--ash-strong);
  letter-spacing: 0.1em;
}

.poster-card__name {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--ink);
  letter-spacing: 0.08em;
  line-height: var(--lh-snug);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.poster-card__qty {
  margin: 0;
  font-size: var(--fs-14);
  color: var(--gold);
  letter-spacing: 0.04em;
}

/* States */
.ledger-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  padding: var(--sp-12) var(--sp-4);
  color: var(--ink-soft);
}
.ledger-state--err   { color: var(--ember); flex-direction: column; }
.ledger-state--empty { color: var(--ink-mute); }
.ledger-state__text  { font-size: var(--fs-14); }

.dossier-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 860px) {
  .poster-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 580px) {
  .poster-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
